import {
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TAB_SHORT_LABELS,
} from 'constants/uiConstants';
import { Tooltip } from 'antd';
import React, { useRef, useState } from 'react';
import {
  PiChartPie,
  PiCurrencyDollar,
  PiLeaf,
  PiScales,
  PiShieldCheck,
} from 'react-icons/pi';
import { RightPanelTabId } from '../../redux/slices/InsightsSlice';

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={14} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={14} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={14} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={14} />,
  [RIGHT_PANEL_TAB_RESISTANCE]: <PiShieldCheck size={14} />,
};

/** rem per button below which labels collapse to icon-only */
const TAB_ICON_THRESHOLD = 3.5;

interface PinnedTabControlProps {
  /** Pinned tabs in their current drag-reorderable order. */
  pinnedTabIds: RightPanelTabId[];
  activePinnedTabId: RightPanelTabId | null;
  /** Pinned panel width in rem — used to decide icon-only mode. */
  panelWidth: number;
  onTabSelect: (tabId: RightPanelTabId) => void;
  onUnpin: (tabId: RightPanelTabId) => void;
  /** Emit the new pinned-only order after a drag reorder. */
  onReorder: (newPinnedOrder: RightPanelTabId[]) => void;
}

const PinnedTabControl: React.FC<PinnedTabControlProps> = ({
  pinnedTabIds,
  activePinnedTabId,
  panelWidth,
  onTabSelect,
  onUnpin,
  onReorder,
}) => {
  const dragTabIdRef = useRef<RightPanelTabId | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<RightPanelTabId | null>(
    null
  );

  const iconOnly = panelWidth / pinnedTabIds.length < TAB_ICON_THRESHOLD;

  const handleDragStart =
    (tabId: RightPanelTabId) => (e: React.DragEvent) => {
      dragTabIdRef.current = tabId;
      e.dataTransfer.effectAllowed = 'move';
    };

  const handleDragOver =
    (tabId: RightPanelTabId) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragTabIdRef.current !== tabId) setDragOverTabId(tabId);
    };

  const handleDrop =
    (dropId: RightPanelTabId) => (e: React.DragEvent) => {
      e.preventDefault();
      const dragId = dragTabIdRef.current;
      if (!dragId || dragId === dropId) {
        setDragOverTabId(null);
        return;
      }
      const next = [...pinnedTabIds];
      const from = next.indexOf(dragId);
      const to = next.indexOf(dropId);
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      onReorder(next);
      dragTabIdRef.current = null;
      setDragOverTabId(null);
    };

  const handleDragEnd = () => {
    dragTabIdRef.current = null;
    setDragOverTabId(null);
  };

  return (
    <div className="flex border-t bg-gray-50">
      {pinnedTabIds.map((tabId) => (
        <Tooltip
          key={tabId}
          title={
            tabId === RIGHT_PANEL_TAB_FINANCIAL
              ? `${RIGHT_PANEL_TAB_LABELS[tabId]} (Plan data only)`
              : RIGHT_PANEL_TAB_LABELS[tabId]
          }
          placement="top"
        >
          {/* Outer wrapper is the group + drag target */}
          <div
            className={`relative flex min-w-0 flex-1 group ${
              dragOverTabId === tabId
                ? 'border-l-2 border-success bg-success-light'
                : ''
            }`}
            onDragOver={handleDragOver(tabId)}
            onDrop={handleDrop(tabId)}
          >
            {/* Main tab button */}
            <button
              type="button"
              draggable
              onDragStart={handleDragStart(tabId)}
              onDragEnd={handleDragEnd}
              onClick={() => onTabSelect(tabId)}
              className={`flex w-full cursor-grab items-center justify-center truncate px-2 py-1.5 text-xs transition-colors active:cursor-grabbing ${
                tabId === activePinnedTabId
                  ? 'border-t-2 border-success bg-white font-semibold text-success'
                  : 'text-gray-muted hover:bg-gray-bright hover:text-gray-dark'
              }`}
            >
              {iconOnly ? TAB_ICONS[tabId] : RIGHT_PANEL_TAB_SHORT_LABELS[tabId]}
            </button>

            {/* Per-tab unpin button — appears on hover */}
            <button
              type="button"
              aria-label={`Unpin ${RIGHT_PANEL_TAB_LABELS[tabId]}`}
              className="absolute right-0.5 top-0.5 z-10 hidden h-3.5 w-3.5 items-center justify-center rounded text-[0.5rem] text-gray-muted hover:bg-gray-200 hover:text-gray-dark group-hover:flex"
              onClick={(e) => {
                e.stopPropagation();
                onUnpin(tabId);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              ✕
            </button>
          </div>
        </Tooltip>
      ))}

    </div>
  );
};

export default PinnedTabControl;
